import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import RefreshIcon from '@mui/icons-material/Refresh';
import LoginIcon from '@mui/icons-material/Login';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

const statusColor = {
  active: 'success',
  suspended: 'warning',
  draft: 'default',
  deleted: 'error',
};

export default function ConsoleClients() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enterTenant, user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    setLoading(true);
    setError('');
    const timeout = setTimeout(() => {
      setError('Ładowanie trwa zbyt długo — sprawdź połączenie lub konsolę przeglądarki (F12).');
      setLoading(false);
    }, 8000);
    try {
      const data = await api.get('/api/tenants');
      clearTimeout(timeout);
      setTenants(Array.isArray(data) ? data : []);
    } catch (err) {
      clearTimeout(timeout);
      console.error('Failed to load tenants', err);
      setError(err.body?.error || err.message || 'Błąd pobierania listy klientów');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return tenants.filter((t) => {
      const matchesSearch =
        !q ||
        (t.name || '').toLowerCase().includes(q) ||
        (t.short_name || '').toLowerCase().includes(q) ||
        (t.domain || '').toLowerCase().includes(q) ||
        String(t.user_count || '').includes(q);
      const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [tenants, search, statusFilter]);

  const handleStatusChange = async (tenant, status) => {
    try {
      await api.put(`/api/tenants/${tenant.id}`, { status });
      await fetchTenants();
    } catch (err) {
      console.error('Status update failed', err);
    }
  };

  const handleDelete = async (tenant) => {
    if (!confirm(`Czy na pewno chcesz usunąć tenant ${tenant.name}?`)) return;
    try {
      await api.del(`/api/tenants/${tenant.id}`);
      await fetchTenants();
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const openEdit = (tenant) => {
    setSelected(tenant);
    setEditForm({
      name: tenant.name || '',
      shortName: tenant.short_name || '',
      domain: tenant.domain || '',
      planId: tenant.plan_id || 'starter',
      status: tenant.status || 'active',
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!selected) return;
    try {
      await api.put(`/api/tenants/${selected.id}`, {
        name: editForm.name,
        shortName: editForm.shortName,
        domain: editForm.domain,
        planId: editForm.planId,
        status: editForm.status,
      });
      setEditOpen(false);
      await fetchTenants();
    } catch (err) {
      console.error('Edit failed', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Klienci
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email} ({user?.role})
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchTenants} disabled={loading}>
            Odśwież
          </Button>
          <Button
            variant="contained"
            startIcon={<AddBusinessIcon />}
            onClick={() => navigate('/console/clients/new')}
          >
            Nowy klient
          </Button>
        </Box>
      </Box>

      <Paper
        sx={{
          p: 2,
          mb: 3,
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
          borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`,
        }}
      >
        <TextField
          placeholder="Szukaj po nazwie, nazwie skróconej, domenie lub liczbie pracowników..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, minWidth: 260 }}
          size="small"
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">Wszystkie</MenuItem>
            <MenuItem value="active">Aktywni</MenuItem>
            <MenuItem value="suspended">Zawieszeni</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      <TableContainer
        component={Paper}
        sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px` }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nazwa</TableCell>
              <TableCell>Nazwa skrócona</TableCell>
              <TableCell>Domena</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Pracownicy</TableCell>
              <TableCell>Kampanie</TableCell>
              <TableCell align="right">Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Brak klientów</Typography>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((tenant) => (
              <TableRow key={tenant.id} hover>
                <TableCell>
                  <Typography fontWeight={600}>{tenant.name}</Typography>
                </TableCell>
                <TableCell>{tenant.short_name || '—'}</TableCell>
                <TableCell>{tenant.domain}</TableCell>
                <TableCell>
                  <Chip label={tenant.plan_id} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip
                    label={tenant.status}
                    color={statusColor[tenant.status] || 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{tenant.user_count || 0}</TableCell>
                <TableCell>
                  {tenant.active_campaign_count || 0} / {tenant.campaign_count || 0}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Wejdź w kontekst tenanta">
                    <IconButton onClick={() => enterTenant(tenant.id)} color="primary">
                      <LoginIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edytuj">
                    <IconButton onClick={() => openEdit(tenant)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  {tenant.status === 'active' ? (
                    <Tooltip title="Zawieś">
                      <IconButton onClick={() => handleStatusChange(tenant, 'suspended')}>
                        <BlockIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Aktywuj">
                      <IconButton onClick={() => handleStatusChange(tenant, 'active')}>
                        <CheckCircleIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Usuń">
                    <IconButton onClick={() => handleDelete(tenant)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edytuj klienta</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Nazwa"
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Nazwa skrócona"
              value={editForm.shortName}
              onChange={(e) => setEditForm((f) => ({ ...f, shortName: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Domena"
              value={editForm.domain}
              onChange={(e) => setEditForm((f) => ({ ...f, domain: e.target.value }))}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Plan</InputLabel>
              <Select
                value={editForm.planId}
                label="Plan"
                onChange={(e) => setEditForm((f) => ({ ...f, planId: e.target.value }))}
              >
                <MenuItem value="starter">Starter</MenuItem>
                <MenuItem value="pro">Pro</MenuItem>
                <MenuItem value="enterprise">Enterprise</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editForm.status}
                label="Status"
                onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
              >
                <MenuItem value="active">Aktywny</MenuItem>
                <MenuItem value="suspended">Zawieszony</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Anuluj</Button>
          <Button variant="contained" onClick={saveEdit}>
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
